import React, { useCallback, useEffect, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { TableCell } from 'modules/ui/Table/TableCell';
import {
  closeConfirmationModalAction,
  closeModalAction,
  openConfirmationModalAction,
  openModalTypedAction,
} from 'store/reducers/modals/actions';
import { useAppDispatch } from 'store';
import { EditAccessModal } from './EditAccessModal';
import { AddAccessModal } from './AddAccessModal';
import { TableWithSearch } from 'modules/ui/TableWithSearch';
import { FlexContainer } from 'styles/FlexContainer';
import { accessAdd, accessEdit } from './constants';
import { accessIcons, accessText } from './constants';
import { useSelector } from 'react-redux';
import {
  deleteByIdAdminSourceAccessAction,
  deleteSourceAccessGroupAction,
  deleteSourceAccessUserAction,
  loadAdminSourceUsersAndGroupsAction,
} from 'store/reducers/adminSources/actions';
import { getAdminActiveSource, getSourceUsersAndGroups } from 'store/reducers/adminSources/getters';
import { AccessInterface } from 'types/types';

export const AccessSource = () => {
  const dispatch = useAppDispatch();
  const { loading, sourcesUsersAndGroupsList } = useSelector(getSourceUsersAndGroups);
  const activeSource = useSelector(getAdminActiveSource);
  const activeSourceId = activeSource?.id;

  useEffect(() => {
    if (activeSourceId) {
      dispatch(loadAdminSourceUsersAndGroupsAction({ id: activeSourceId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSourceId]);

  const onCloseEditAccessModal = () => dispatch(closeModalAction(accessEdit));
  const onCloseAddAccessModal = () => dispatch(closeModalAction(accessAdd));

  const onDelete = useCallback(
    async (sourceAccess: AccessInterface) => {
      try {
        const flowAccessId = sourceAccess.id;

        const response =
          sourceAccess.entity === 'group'
            ? await dispatch(deleteSourceAccessGroupAction({ sourceId: activeSourceId || '', groupId: flowAccessId })).unwrap()
            : await dispatch(deleteSourceAccessUserAction({ sourceId: activeSourceId || '', userId: flowAccessId })).unwrap();

        if (response) {
          dispatch(deleteByIdAdminSourceAccessAction(flowAccessId));
        }
      } catch (error) {
        console.error('Error creating project version:', error);
      } finally {
        dispatch(closeConfirmationModalAction());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeSourceId],
  );

  const onDeleteModal = useCallback(
    async (sourceAccess: AccessInterface) => {
      dispatch(
        openConfirmationModalAction({
          confirmationButtonText: 'Удалить',
          description: `Действительно удалить доступ «${sourceAccess.name}» к группе?`,
          onConfirm: () => onDelete(sourceAccess),
          titleText: 'Удаление доступа',
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDelete],
  );

  const onEditModal = useCallback(
    (sourceAccess: AccessInterface) =>
      dispatch(
        openModalTypedAction({
          Component: EditAccessModal,
          componentProps: {
            onClose: onCloseEditAccessModal,
            sourceAccess,
          },
          modalSettings: {
            headerText: 'Изменение прав доступа',
            width: '320px',
          },
          name: accessEdit,
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCloseEditAccessModal],
  );

  const onAddModal = useCallback(
    () =>
      dispatch(
        openModalTypedAction({
          Component: AddAccessModal,
          componentProps: {
            onClose: onCloseAddAccessModal,
          },
          modalSettings: {
            headerText: 'Добавление доступа',
          },
          name: accessAdd,
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCloseAddAccessModal],
  );

  const columnHelper = createColumnHelper<AccessInterface>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Группа/пользователь',
        cell: (info) => {
          const rowData = info.row.original;

          return (
            <TableCell
              text={info.getValue()}
              Icon={accessIcons[rowData.entity]}
              actions={{
                onRemove: async () => {
                  await onDeleteModal(rowData);
                },
              }}
            />
          );
        },
      }),
      columnHelper.accessor('type', {
        header: 'Права',
        cell: (info) => (
          <TableCell
            text={accessText[info.getValue()]}
            actions={{
              onEdit: async () => {
                await onEditModal(info.row.original);
              },
            }}
          />
        ),
      }),
    ],
    [columnHelper, onDeleteModal, onEditModal],
  );

  return (
    <FlexContainer position="relative" width="100%" padding="24px" maxWidth="800px">
      <TableWithSearch
        filterColumnName="name"
        columns={columns}
        rows={sourcesUsersAndGroupsList}
        onAddModal={onAddModal}
        loading={loading}
      />
    </FlexContainer>
  );
};
